import {Component, OnInit, OnDestroy} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import BusinessEntity from 'src/app/services/businessEntity.model';
import {BusinessEntityService} from 'src/app/services/businessEntity.service';
import CustomerPondModel from 'src/app/services/customerPond.model';
import {CustomerPondService} from 'src/app/services/customerPond.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import {FarmSiteService} from 'src/app/services/farmSite.service';
import PondCultureModel from 'src/app/services/pondCulture.model';
import {PondCultureService} from 'src/app/services/pondCulture.service';
import TransactionsModel from 'src/app/services/transactions.model';
import {TransactionsService} from 'src/app/services/transactions.service';
import * as moment from 'moment';
import * as Chart from 'chart.js';
import 'chartjs-plugin-labels';
import * as d3pie from 'src/assets/d3pie.js';
import {ToastrService} from 'ngx-toastr';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * @component FarmSpendAnalyzerComponent
 * @selector app-farm-spend-analyzer
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `FarmSpendAnalyzerComponent` is a comprehensive analytical tool designed to visualize and report on farm expenditures.
 * It provides insights into spending patterns across different categories (e.g., Feed, Treatment, Seed, Energy) and allows for
 * detailed analysis at both the farm level and individual pond level.
 *
 * ## Key Responsibilities:
 * 1. **Data Visualization**: Utilizes Chart.js and D3.js to render interactive pie charts representing expenditure distribution.
 * 2. **Hierarchical Analysis**: Supports drill-down analysis from overall farm spend to specific pond-level transactions.
 * 3. **Filtering**: Offers robust filtering capabilities including:
 *    - Business and Farm Site selection.
 *    - Specific Pond and Culture selection.
 *    - Date range filtering for custom time periods.
 * 4. **Cost Calculation**: Computes and displays key financial metrics such as Total Expenditure, Total Biomass, and Production Cost.
 * 5. **Role-Based Access**: Adapts data visibility and available options based on the user's role (Farmer, Farm Manager, Business Admin).
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Access to all farm sites and financial data for their business.
 *   - **Farm Manager**: Restricted access to farm sites assigned to them.
 * - **Other Categories**:
 *   - **Employee**: Can search for and select businesses to view spend analysis (if authorized).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context ('Farm').
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for display and context.
 * - `LoginUserName`: Used for display.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` determines user context and loads initial farm sites or businesses.
 * 2. **Selection**: User selects Business, Farm Site, and optionally a Pond/Culture.
 * 3. **Analysis Trigger**: `onSubmit` triggers the analysis process.
 *    - If a pond is selected, `getpondspendanalysis` is called.
 *    - Otherwise, `farmSpendAnalysis` is called for the entire farm.
 * 4. **Data Processing**:
 *    - `arrangeCategoryData` processes raw transaction data into categories for charting.
 *    - `getCategoriesPieChart` renders the main category distribution chart.
 *    - `itemsSelected` handles drill-down into specific categories (e.g., breaking down 'Feed' into types).
 *
 * ## UI State Management:
 * - **Loading**: `pgLoad` and `searchLoad` manage loading indicators.
 * - **Charts**: Manages Chart.js instances (`CategoriesPieChart`) and D3 charts (`itemPieChart`).
 * - **Form**: `registerForm` handles selection criteria and validation.
 */
@Component({
  selector: 'app-farm-spend-analyzer',
  templateUrl: './farm-spend-analyzer.component.html',
  styleUrls: ['./farm-spend-analyzer.component.scss']
})
export class FarmSpendAnalyzerComponent implements OnInit, OnDestroy {
  /** @type {boolean} Flag for page loading state. */
  public pgLoad = false;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} Name of the logged-in user. */
  public LoginUserName: string;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {Array<BusinessEntity>} List of available business entities for selection. */
  public businessEntites: Array<BusinessEntity>;

  /** @type {Array<FarmSiteModel>} List of farm sites associated with the selected business. */
  public farmsites: Array<FarmSiteModel>;

  /** @type {FarmSiteModel} Selected farm site model (unused in current logic). */
  public farmsite: FarmSiteModel;

  /** @type {Array<CustomerPondModel>} List of customer ponds for the selected farm site. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {Array<PondCultureModel>} List of cultures associated with the selected pond. */
  public pondCultures: Array<PondCultureModel>;

  /** @type {PondCultureModel} Selected pond culture model (unused in current logic). */
  public pondCulture: PondCultureModel;

  /** @type {Array<TransactionsModel>} List of transactions (unused in current logic). */
  public transactions: Array<TransactionsModel>;

  /** @type {TransactionsModel} Model containing farm spend analysis data. */
  public farmSpendData: TransactionsModel;

  /** @type {TransactionsModel} Model used for binding filter criteria. */
  public transaction: TransactionsModel;

  /** @type {boolean} Flag to toggle between single date picker and date range picker. */
  public singleDatePickerFlag: boolean = false;

  /** @type {FormGroup} Form group for the date picker. */
  public datePickerForm: FormGroup;

  /** @type {any} Object to store API response data. */
  public apiData: any = {};

  /** @type {boolean} Flag indicating if analysis is for a specific pond. */
  public pondFlag: boolean = false;

  /** @type {boolean} Flag indicating if analysis is for the entire farm (unused). */
  public farmSpendFlag: boolean = false;

  /** @type {boolean} Flag for search loading state. */
  public searchLoad: boolean;

  /** @type {any} Total expenditure value for display. */
  public totalExpenditure: any = 0;

  /** @type {any} Total biomass value for display. */
  public totalBiomass: any = 0;

  /** @type {any} Production cost value for display. */
  public productionCost: any = 0;

  /** @type {number} Pond usage status (unused in current logic). */
  public ponduse = 0;

  /** @type {Array<any>} Array to store 'Others' category items. */
  public othersArray = [];

  /** @type {any} Chart.js instance for the categories pie chart. */
  public CategoriesPieChart = null;

  /** @type {any} D3.js chart instance for the items pie chart. */
  public itemPieChart = null;

  /** @type {string} Keyword for search operations. */
  public keyword: string;

  /** @type {any} Generic item holder. */
  public item: any;

  /** @type {boolean} Flag to always show calendars in date picker. */
  alwaysShowCalendars: boolean;

  /** @type {string[]} Array of predefined expense categories. */
  public categoriesArray = ['Feed', 'Treatment', 'Seed', 'Energy', 'Salaries', 'Others'];

  /** @type {string[]} Array of colors for the category pie chart. */
  public categoryPieColors = ['#F97F51', '#3B3B98', '#25CCF7', '#EAB543', '#55E6C1', '#D6A2E8'];

  /** @type {any[]} Array to store processed category results for charting. */
  public categoriesResultArray: any = [];

  /** @type {any[]} Array to store processed item results for drill-down charting. */
  public itemsResultsArray: any = [];

  /** @type {boolean} Flag indicating if an item category is selected for drill-down. */
  public itemFlag: boolean = false;

  /** @type {any[]} Array to store content for the items pie chart. */
  public itemsPiecontent: any = [];

  /** @type {boolean} Flag indicating if category data is available. */
  public categoryFlag: boolean = true;

  /** @type {boolean} Flag indicating if a search has been performed. */
  public searchFlag = false;

  /** @type {boolean} Flag indicating if a pond is selected. */
  public pondSelectedFlag: boolean = false;

  /** @type {any} Currently selected item category for drill-down. */
  public selectedItem = null;

  /** @type {boolean} Flag for dropdown visibility. */
  public isDropdownOpen = false;

  /** @type {any[]} Filtered options for business dropdown. */
  public filteredOptions: any[] = [];

  /** @type {string} Business search input placeholder/value. */
  public businessSearch: string = 'Enter the business name';

  /** @type {boolean} Flag indicating if the form has been submitted. */
  public submitted: boolean;

  /** @type {FormGroup} Main reactive form for analysis criteria. */
  public registerForm: FormGroup;

  /** @type {boolean} Flag to disable the submit button during processing. */
  public submitButtonDisabled: boolean;

  /** @type {string} Formatted total expense cost value. */
  public totalExpenseCostValue: string;

  /** @type {{ startDate: moment.Moment, endDate: moment.Moment }} Selected date range. */
  selected: { startDate: moment.Moment, endDate: moment.Moment };

  /** @type {string | null} Formatted start date string. */
  public fromDate: string | null = null;

  /** @type {string | null} Formatted end date string. */
  public toDate: string | null = null;

  /** @type {FormControl} Control for transaction date (single date picker). */
  transactionDateControl = new FormControl();

  /** @type {boolean} Flag indicating if ponds are available for the selected farm site. */
  pondsAvailable = true;

  /** @type {Date} Maximum allowed date for date picker. */
  maxDate: Date = new Date();

  /**
   * Constructor for FarmSpendAnalyzerComponent.
   * Initializes services and the reactive form.
   *
   * @param {ToastrService} toastrService - Service for toast notifications.
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {CustomerPondService} customerPondService - Service for customer pond operations.
   * @param {PondCultureService} pondCultureService - Service for pond culture operations.
   * @param {TransactionsService} transactionService - Service for transaction operations.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   *
   * @service ToastrService
   * @service ActivatedRoute
   * @service FormBuilder
   * @service BusinessEntityService
   * @service FarmSiteService
   * @service CustomerPondService
   * @service PondCultureService
   * @service TransactionsService
   * @service CommonMethodsService
   * @service LocationStrategy
   * @service PreviousRouteService
   */
  constructor(private toastrService: ToastrService, public route: ActivatedRoute, public formBuilder: FormBuilder, public businessEntityService: BusinessEntityService, public farmSiteService: FarmSiteService, public customerPondService: CustomerPondService, public pondCultureService: PondCultureService, public transactionService: TransactionsService,
              public commonMethodsService: CommonMethodsService, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.transaction = new TransactionsModel();
    // Initialize the register form with validation
    this.registerForm = this.formBuilder.group({
      businessfield: ['', Validators.required],
      location: ['', Validators.required],
      pond: [null],
      culture: [null],
      dateRangeField: this.formBuilder.group({
        start: [null],
        end: [null]
      })
    });
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Initializes date picker and default values.
   * - Sets up the previous route.
   * - Determines logic flow based on `loginBusinessCategory`:
   *   - **Farm**: Auto-sets business ID and fetches farm sites based on `loginPersonType`.
   *   - **Default**: Fetches all business entities for selection.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  async ngOnInit() {
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.loginPersonType = localStorage.getItem('loginPersonType');
    this.LoginUserName = localStorage.getItem('LoginUserName');
    this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
    this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
    this.alwaysShowCalendars = true;
    this.searchLoad = false;
    this.keyword = 'businessName';
    this.totalExpenseCostValue = undefined;
    this.datePickerForm = new FormGroup({date: new FormControl(null)});
    this.initializeDatePicker();
    // this.datePickerForm.get('date').setValue({startDate:  moment().subtract(364, 'days').startOf('day'),endDate: moment()})
    try {
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.transaction.business_id = localStorage.businessID;
        this.pondCultures = new Array();
        this.customerPonds = new Array();
        this.registerForm.get('businessfield').patchValue(this.loginUserBusinessName);
        this.farmsites = await this.farmSiteService.getFarmeSitesByFarmIdInFarmSpend(localStorage.businessID);
        this.pgLoad = true;
        this.setSingleValueForFormLocation();
      } else if (this.loginPersonType == 'Farm Manager' && this.loginUserBusinessCategory == 'Farm') {
        this.transaction.business_id = localStorage.businessID;
        this.customerPonds = new Array();
        this.pondCultures = new Array();
        this.registerForm.get('businessfield').patchValue(this.loginUserBusinessName);
        this.farmsites = await this.farmSiteService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.businessID, localStorage.personID);
        this.pgLoad = true;
        this.setSingleValueForFormLocation();
      } else {
        this.transaction.costManagement = new Array();
        this.customerPonds = new Array();
        this.pondCultures = new Array();
        this.businessSearch = undefined;
        this.registerForm.get('businessfield').patchValue('');
        let farmBusiness = await this.businessEntityService.getAllBusiness();
        this.businessEntites = farmBusiness.filter(businessEntity => businessEntity.businessType && businessEntity.businessType.length > 0 && businessEntity.businessType[0].businessTypeID && businessEntity.businessType[0].businessTypeID === '618916f2b4391c6d48137e3d');
        this.pgLoad = true;
        this.filteredOptions = this.businessEntites;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the HTML template.
   * @returns {any} The controls of the `registerForm`.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method filterBusinesses
   * @description Filters the `businessEntites` list based on the user's input in the business field.
   * Updates `filteredOptions` with the result.
   * @side-effect Updates `filteredOptions`.
   */
  public filterBusinesses(): void {
    const value = (this.registerForm.get('businessfield')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businessEntites.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method selectOption
   * @description Handles the selection of a business entity from the dropdown.
   * Updates the form value and triggers fetching of farm sites.
   * @param {BusinessEntity} business - The selected business entity.
   * @side-effect Updates `registerForm`, `isDropdownOpen`, `businessSearch`, `transaction.business_id`, calls `getAllFarmsitesBybusinessId`.
   */
  public selectOption(business: BusinessEntity): void {
    this.registerForm.get('businessfield')!.setValue(business.businessName);
    $('.custom-select label').css({'top': 0});
    this.isDropdownOpen = false;
    this.businessSearch = business.businessName;
    this.transaction.business_id = business.id;
    if (this.transaction.business_id) {
      this.getAllFarmsitesBybusinessId();
    }
  }


  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests.
   */
  public ngOnDestroy(): void {
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.customerPondService.cancelOnGoingHttpRequests();
    this.pondCultureService.cancelOnGoingHttpRequests();
    this.transactionService.cancelOnGoingHttpRequests();
  }

  /**
   * @method setSingleValueForFormLocation
   * @async
   * @description Auto-selects the farm site if only one exists and triggers pond fetching.
   */
  public async setSingleValueForFormLocation() {
    if (this.farmsites && this.farmsites.length == 1) {
      this.transaction.farm_site_id = this.farmsites[0].id; // model-binding key
      await this.getAllpondsByFarmsiteId();
    }
  }

  /**
   * @method getAllpondsByFarmsiteId
   * @async
   * @description Fetches ponds associated with the selected farm site ID.
   * Populates `customerPonds` and handles date picker configuration based on pond availability.
   * @side-effect Updates `customerPonds`, `pondsAvailable`, `singleDatePickerFlag`, calls `getCultureByPondId`.
   */
  public async getAllpondsByFarmsiteId() {
    try {
      this.customerPonds = new Array();
      this.pondCultures = new Array();
      this.transaction.pond_id = undefined;
      this.transaction.pond_culture_id = undefined;
      let customerPondsData = await this.customerPondService.getPondsByFarmSiteId([this.transaction.farm_site_id]);
      this.customerPonds = new Array();
      if (customerPondsData && customerPondsData.length > 0) {
        for (let i = 0; i < customerPondsData.length; i++) {
          if ((customerPondsData[i].PondCultureData && customerPondsData[i].PondCultureData.length > 0)) {
            let eachpondculture: any = customerPondsData[i].PondCultureData[customerPondsData[i].PondCultureData.length - 1];
            if (eachpondculture) {
              eachpondculture.name = customerPondsData[i].name;
              eachpondculture.pond_id = customerPondsData[i].id;
              this.customerPonds.push(eachpondculture);
            }
            // }
          } else if (customerPondsData[i].isPreparatory == true) {
            let isPreObj: any = {
              pond_id: customerPondsData[i].id,
              name: customerPondsData[i].name,
            };
            this.customerPonds.push(isPreObj);
          }
        }
      }
      // Update flag for showing error message
      this.pondsAvailable = this.customerPonds.length > 0;
      // If NO ponds → switch to date range and apply 1 year default
      if (!this.pondsAvailable) {
        this.singleDatePickerFlag = false;
        this.initializeDatePicker(); // applies 1 year range
      }
      this.getCultureByPondId();
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForCulture
   * @description Auto-selects the culture ID if only one culture exists for the selected pond.
   */
  public setSingleValueForCulture() {
    if (this.pondCultures && this.pondCultures.length == 1) {
      this.transaction.pond_culture_id = this.pondCultures[0].id; // model-binding key
    }
  }

  /**
   * @method getCultureByPondId
   * @async
   * @description Fetches cultures for the selected pond ID.
   * Configures date picker mode (single vs range) based on pond selection.
   */
  public async getCultureByPondId() {
    this.transaction.pond_culture_id = undefined;
    this.pondCultures = new Array();
    try {
      if (this.transaction.pond_id) {
        this.singleDatePickerFlag = true;
        this.datePickerForm.controls['date'].disable();
        this.datePickerForm.get('date').setValue({startDate: moment(), endDate: moment()});
        this.pondCultures = await this.pondCultureService.getCulturesByPondId(Number(this.transaction.pond_id));
        this.setSingleValueForCulture();
      } else {
        this.pondCultures = [];
        this.transaction.pond_culture_id = undefined;
        this.singleDatePickerFlag = false;
        this.datePickerForm.controls['date'].enable();
        this.datePickerForm.get('date').setValue({
          startDate: moment().subtract(364, 'days').startOf('day'),
          endDate: moment()
        });
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllFarmsitesBybusinessId
   * @async
   * @description Fetches farm sites for the selected business ID (Admin/Employee flow).
   */
  public async getAllFarmsitesBybusinessId() {
    this.transaction.costManagement = new Array();
    this.customerPonds = new Array();
    this.pondCultures = new Array();
    this.transaction.farm_site_id = undefined;
    this.transaction.pond_id = undefined;
    this.transaction.pond_culture_id = undefined;
    try {
      this.farmsites = await this.farmSiteService.getFarmeSitesByFarmId(this.transaction.business_id);
      this.pgLoad = true;
      this.setSingleValueForFormLocation();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method onSubmit
   * @async
   * @description Handles form submission to trigger spend analysis.
   * Validates form and routes to either `getpondspendanalysis` or `farmSpendAnalysis` based on selection.
   */
  public async onSubmit() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) {
      return;
    }
    this.searchLoad = true;
    this.searchFlag = false;
    this.pondSelectedFlag = false;
    this.categoriesResultArray = [];
    this.itemsResultsArray = [];
    this.categoryFlag = false;
    this.submitButtonDisabled = true;
    this.itemFlag = false;
    if (this.transaction.pond_id != null && this.transaction.pond_id != undefined && this.transaction.pond_id) {
      this.pondSelectedFlag = true;
      this.totalExpenditure = 0;
      this.totalBiomass = 0;
      this.productionCost = 0;
      this.ponduse = 0;
      await this.getpondspendanalysis();
    } else {
      await this.farmSpendAnalysis();
    }
  }

  /**
   * @method getpondspendanalysis
   * @async
   * @description Fetches and processes spend analysis data for a specific pond.
   * Calculates total expenditure, biomass, and production cost.
   */
  public async getpondspendanalysis() {
    try {
      this.totalExpenseCostValue = undefined;
      this.transaction.pondIdInputDate = moment(this.transactionDateControl.value).format('YYYY-MM-DD');
      if (this.transaction.pond_culture_id == undefined && this.pondCultures.length > 0) {
        this.transaction.pond_culture_id = this.pondCultures[this.pondCultures.length - 1].id.toString();
      }
      let pondTransactionsKey: any = await this.transactionService.getPondTransactions(this.transaction.business_id, this.transaction.farm_site_id, Number(this.transaction.pond_id), Number(this.transaction.pond_culture_id), this.transaction.pondIdInputDate);
      this.submitButtonDisabled = false;
      if (pondTransactionsKey) {
        if (pondTransactionsKey.pondTransactions && pondTransactionsKey.pondTransactions.length > 0) {
          this.totalExpenseCostValue = new Intl.NumberFormat('en-IN').format(pondTransactionsKey && pondTransactionsKey.totalExpenditure.toFixed(0));
          this.transaction.pondTransactions = pondTransactionsKey.pondTransactions;
          this.transaction.tank_name = pondTransactionsKey.pondTransactions[0] && pondTransactionsKey.pondTransactions[0].TankName;
          this.apiData = {};
          this.apiData = pondTransactionsKey;
          this.pondFlag = true;
          this.transaction.from_date = moment(this.transactionDateControl.value).format('YYYY-MM-DD');
          this.transaction.to_date = moment(this.transactionDateControl.value).format('YYYY-MM-DD');
          let pondSpendAnalysisPopup = await this.transactionService.getPondSpendAnalysisPopup(this.transaction.business_id, this.transaction.farm_site_id, this.transaction.from_date, this.transaction.to_date);
          if (pondSpendAnalysisPopup?.transactions.length > 0) {
            let getcurrentPondData: any = pondSpendAnalysisPopup.transactions.filter(obj => Number(obj.pond_id) == Number(this.transaction.pond_id) && obj.pond_culture_id == this.transaction.pond_culture_id);
            this.totalExpenditure = new Intl.NumberFormat('en-IN').format(Math.round(getcurrentPondData[0].TOT_EXP ? getcurrentPondData[0].TOT_EXP : 0));
            this.totalBiomass = new Intl.NumberFormat('en-IN').format(getcurrentPondData[0].TBM ? getcurrentPondData[0].TBM : 0);
            this.ponduse = getcurrentPondData[0].ponduse;
            if (this.ponduse == 2) {
              this.productionCost = new Intl.NumberFormat('en-IN').format(getcurrentPondData[0].productioncostperpiece.toFixed(3));
            } else {
              this.productionCost = new Intl.NumberFormat('en-IN').format(getcurrentPondData[0].Productioncost.toFixed(3));
            }
          }
          this.arrangeCategoryData(pondTransactionsKey);
        } else {
          this.categoryFlag = false;
          this.toastrService.warning('Data not available.');
          this.searchLoad = false;
          this.searchFlag = true;
        }
      } else {
        this.commonMethodsService.userFriendlyAlertMessages('Unable to fetch data please try again after some time');
        this.searchLoad = false;
        this.submitButtonDisabled = false;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.searchLoad = false;
      this.submitButtonDisabled = false;
    }
  }

  /**
   * @method farmSpendAnalysis
   * @async
   * @description Fetches and processes spend analysis data for the entire farm (or selected date range).
   * Aggregates costs across all ponds/categories.
   */
  public async farmSpendAnalysis() {
    try {
      this.totalExpenseCostValue = undefined;
      this.transaction.from_date = undefined;
      this.transaction.to_date = undefined;
      this.transaction.from_date = undefined;
      this.transaction.to_date = undefined;
      const { start, end } = this.dateRangeField.value;

      if (start) {
        this.transaction.from_date = moment(start).format('YYYY-MM-DD');
      }

      if (end) {
        this.transaction.to_date = moment(end).format('YYYY-MM-DD');
      }
      this.farmSpendData = await this.transactionService.getFarmSpendAnalysis(this.transaction.business_id, this.transaction.farm_site_id, this.transaction.from_date, this.transaction.to_date);
      this.submitButtonDisabled = false;
      if (this.farmSpendData) {
        if (this.farmSpendData.costManagement && this.farmSpendData.costManagement.length > 0) {
          this.totalExpenseCostValue = new Intl.NumberFormat('en-IN').format(Number(this.farmSpendData && this.farmSpendData.totalExpenditure.toFixed(0)));
          if (this.farmSpendData.totalExpenditure) {
            this.transaction.totalExpenditureValue = '₹ ' + (Number(this.farmSpendData.totalExpenditure).toLocaleString('en-IN'));
          } else {
            this.transaction.totalExpenditureValue = '₹ ' + 0;
          }
          this.apiData = {};
          this.apiData = this.farmSpendData;
          this.pondFlag = false;
          this.arrangeCategoryData(this.farmSpendData);
        } else {
          this.toastrService.warning('Data not available.');
          this.categoryFlag = false;
          this.searchLoad = false;
          this.searchFlag = true;
        }
      } else {
        this.commonMethodsService.userFriendlyAlertMessages('Unable to fetch data please try again after some time');
        this.searchLoad = false;
        this.submitButtonDisabled = false;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.searchLoad = false;
      this.submitButtonDisabled = false;
    }
  }

  /**
   * @method arrangeCategoryData
   * @description Processes raw spend data into categories for visualization.
   * Aggregates expenses into predefined categories (Feed, Treatment, etc.) and prepares data for the pie chart.
   * @param {any} spendData - The raw spend data from the API.
   */
  public arrangeCategoryData(spendData) {
    this.searchFlag = true;
    let categoryFilterArray: any = [];
    let categorieRecords: any = [];
    let tempCatArray = ['Feed', 'Powder Feed', 'Live Feed', 'Treatment', 'Feed Mix', 'probiotic', 'Multi Vitamin', 'Chemical', 'Others', 'Energy', 'Seed', 'Transportation', 'Salaries', 'Groceries & Food', 'Labour', 'Chemicals & Tests', 'Software', 'Algae  Purpose', 'Water Purpose', 'Hardware Related', 'Tractor'];
    tempCatArray.forEach((item) => {
      if (this.pondFlag) {
        categorieRecords = spendData.pondTransactions.filter(x => x.itemtype == item);
      } else {
        categorieRecords = spendData.costManagement.filter(x => x.itemtype == item);
      }
      if (categorieRecords.length > 0) {
        let val = Math.round(categorieRecords.reduce((sum, item) => sum + item.expensecost, 0));
        let eleObj = {categoryName: item, expense: val};
        categoryFilterArray.push(eleObj);
      } else {
        let eleObj = {categoryName: item, expense: 0};
        categoryFilterArray.push(eleObj);
      }
    });
    let catArray = [];
    this.categoriesResultArray = [];
    this.categoriesArray.forEach((item) => {
      if (item == 'Seed' || item == 'Energy') {
        let categorieItemRecords: any = categoryFilterArray.filter(x => x.categoryName == item);
        let ele = {categoryName: item, expense: categorieItemRecords[0].expense};
        catArray.push(ele);
      }
      if (item == 'Treatment') {
        let categorieItemRecords = categoryFilterArray.filter(x => x.categoryName == item);
        let categorieFeedMixRecords = categoryFilterArray.filter(x => x.categoryName == 'Feed Mix');
        let categorieProbioticRecord = categoryFilterArray.filter(x => x.categoryName == 'probiotic');
        let categorieMultivitaminRecord = categoryFilterArray.filter(x => x.categoryName == 'Multi Vitamin');
        let categorieChemicalRecord = categoryFilterArray.filter(x => x.categoryName == 'Chemical');
        let ele = {
          categoryName: 'Applications',
          expense: categorieItemRecords[0].expense + categorieProbioticRecord[0].expense + categorieMultivitaminRecord[0].expense + categorieChemicalRecord[0].expense + categorieFeedMixRecords[0].expense
        };
        catArray.push(ele);
      }
      if (item == 'Feed') {
        let categorieFeedRecords = categoryFilterArray.filter(x => x.categoryName == item);
        let categoriePowderRecord = categoryFilterArray.filter(x => x.categoryName == 'Powder Feed');
        let categorieLiveRecord = categoryFilterArray.filter(x => x.categoryName == 'Live Feed');
        let ele = {
          categoryName: item,
          expense: categorieFeedRecords[0].expense + categoriePowderRecord[0].expense + categorieLiveRecord[0].expense
        };
        catArray.push(ele);
      }
      if (item == 'Salaries') {
        let categorieSalaryRecords = categoryFilterArray.filter(x => x.categoryName == item);
        let categorieLaborRecord = categoryFilterArray.filter(x => x.categoryName == 'Labour');
        let ele = {categoryName: item, expense: categorieSalaryRecords[0].expense + categorieLaborRecord[0].expense};
        catArray.push(ele);
      }
      if (item == 'Others') {
        let categorieOtherRecords = categoryFilterArray.filter(x => x.categoryName == item);
        let categorieTransportationRecord = categoryFilterArray.filter(x => x.categoryName == 'Transportation');
        let categorieGroceriesRecord = categoryFilterArray.filter(x => x.categoryName == 'Groceries & Food');
        let categorieChemicalsRecord = categoryFilterArray.filter(x => x.categoryName == 'Chemicals & Tests');
        let categorieSoftwareRecord = categoryFilterArray.filter(x => x.categoryName == 'Software');
        let categorieTractorRecord = categoryFilterArray.filter(x => x.categoryName == 'Tractor');
        let categorieHardwareRelatedRecord = categoryFilterArray.filter(x => x.categoryName == 'Hardware Related');
        let categorieWaterPurposeRecord = categoryFilterArray.filter(x => x.categoryName == 'Water Purpose');
        let categorieAlgaePurposeRecord = categoryFilterArray.filter(x => x.categoryName == 'Algae  Purpose');
        let sum = categorieOtherRecords[0].expense + categorieTransportationRecord[0].expense + categorieGroceriesRecord[0].expense + categorieChemicalsRecord[0].expense + categorieSoftwareRecord[0].expense + categorieHardwareRelatedRecord[0].expense + categorieAlgaePurposeRecord[0].expense + categorieWaterPurposeRecord[0].expense + categorieTractorRecord[0].expense;
        let ele = {categoryName: item, expense: sum};
        catArray.push(ele);
      }
    });

    let overallExpen = catArray.reduce(function (sum, tax) {
      return sum + tax.expense;
    }, 0);
    let otherVal = 0;
    this.othersArray = [];
    catArray.forEach((item, index) => {
      let perCentage = ((item.expense / overallExpen) * 100).toFixed(2);
      if (item.categoryName === 'Others' || Number(perCentage) < 5) {
        this.othersArray.push(item.categoryName);
        otherVal = otherVal + item.expense;
      } else {
        let ele = {
          categoryName: item.categoryName,
          expense: item.expense,
          percentage: ((item.expense / overallExpen) * 100).toFixed(2),
          color: this.categoryPieColors[index],
          displayExpense: new Intl.NumberFormat('en-IN').format(item.expense)
        };
        this.categoriesResultArray.push(ele);
      }
    });
    if (otherVal > 0) {
      this.categoriesResultArray.push({
        categoryName: 'Others',
        expense: parseFloat(otherVal.toFixed(2)),
        percentage: parseFloat(((otherVal / overallExpen) * 100).toFixed(2)),
        color: this.categoryPieColors[this.categoryPieColors.length - 1],
        displayExpense: new Intl.NumberFormat('en-IN').format(parseFloat(otherVal.toFixed(2)))
      });
    }
    this.searchLoad = false;
    this.getCategoriesPieChart();
  }

  /**
   * @method getCategoriesPieChart
   * @description Initializes and renders the Chart.js pie chart for expense categories.
   * Configures chart options, tooltips, and click events for drill-down.
   */
  public getCategoriesPieChart() {
    if (this.CategoriesPieChart != null) {
      this.CategoriesPieChart.destroy();
    }
    this.itemFlag = false;
    this.categoryFlag = true;
    let labels = [];
    let colors = [];
    let data = [];
    let finalCatArray = [];
    this.categoriesResultArray.forEach((item) => {
      if (item.expense > 0) {
        labels.push(item.categoryName);
        colors.push(item.color);
        data.push(item.expense);
        finalCatArray.push(item);
      }
    });
    try {

      let ele: any = document.getElementById('pie-chart');
      this.CategoriesPieChart = new Chart(ele, {
        type: 'pie',
        data: {
          labels: labels,
          datasets: [{
            label: 'Expenses (Rupees)',
            backgroundColor: colors,
            data: data,
            hoverBorderColor: 'transparent',
            hoverBorderWidth: 10,
          }]
        },
        options: {
          legend: {display: false},
          legendCallback: function () {
            return 'hello';
          },
          title: {display: false},
          tooltips: {enabled: false},
          events: ['click'],
          layout: {padding: 30},
          responsive: true,
          onClick: (evt, item) => {
            var activePoints = this.CategoriesPieChart.getElementsAtEvent(evt);
            var selectedIndex = activePoints[0]._index;
            var selectedLabel = labels[selectedIndex];
            if (item.length > 0 && selectedLabel != this.selectedItem) {
              this.CategoriesPieChart.update();
              let ele: any = item;
              ele[0]._model.outerRadius += 20;
              ele[0]._model.innerRadius += 15;
              ele[0]._model.borderWidth = 10;
              item = ele;
              this.itemsSelected(finalCatArray[selectedIndex]);
              this.itemFlag = true;
            }
          },
          plugins: {
            labels: [
              {
                render: 'label',
                fontColor: ['#0E0F0F', '#0E0F0F', '#0E0F0F', '#0E0F0F', '#0E0F0F', '#0E0F0F'],
                position: 'outside',
                fontSize: 25,
                outsidePadding: 4,
                textMargin: 8,
                arc: true
              },
              {
                render: function (args) {
                  return '    ' + args.percentage + '%';
                },
                fontColor: ['white', 'white', 'white', 'white', 'white', 'white'],
                position: 'border',
                fontSize: 30,
                precision: 2,
                textMargin: 8
              }]
          }
        },
      });
    } catch (error) {
      this.toastrService.error(error, '', {
        timeOut: 3000
      });
    }
  }

  /**
   * @method itemsSelected
   * @description Handles the selection of a category slice in the pie chart.
   * Drills down into the selected category to show detailed item expenses.
   * @param {any} item - The selected category item.
   */
  public itemsSelected(item: any) {
    if (item) {
      this.selectedItem = item.categoryName;
      this.itemsResultsArray = [];
      const colors = ['#F97F51', '#3B3B98', '#EAB543', '#25CCF7', '#55E6C1', '#D6A2E8'];
      let tempArray = [];
      let serverData: any;
      if (this.pondFlag) {
        serverData = this.apiData.pondTransactions;
      } else {
        serverData = this.apiData.costManagement;
      }
      if (item.categoryName === 'Others') {
        let categorieOtherRecords: any = serverData.filter(x => x.itemtype == item.categoryName);
        tempArray = tempArray.concat(categorieOtherRecords);
        let categorieTransportationRecord = serverData.filter(x => x.itemtype == 'Transportation');
        tempArray = tempArray.concat(categorieTransportationRecord);
        let categorieGroceriesRecord = serverData.filter(x => x.itemtype == 'Groceries & Food');
        tempArray = tempArray.concat(categorieGroceriesRecord);
        let categorieChemicalsRecord = serverData.filter(x => x.itemtype == 'Chemicals & Tests');
        tempArray = tempArray.concat(categorieChemicalsRecord);
        let categorieSoftwareRecord = serverData.filter(x => x.itemtype == 'Software');
        tempArray = tempArray.concat(categorieSoftwareRecord);
        let categoriesWaterPurposeRecord = serverData.filter(x => x.itemtype == 'Water Purpose');
        tempArray = tempArray.concat(categoriesWaterPurposeRecord);
        let categoriesAlgaePurposeRecord = serverData.filter(x => x.itemtype == 'Algae  Purpose');
        tempArray = tempArray.concat(categoriesAlgaePurposeRecord);
        let categoriesHardwareRecord = serverData.filter(x => x.itemtype == 'Hardware Related');
        tempArray = tempArray.concat(categoriesHardwareRecord);
        let categoriesTractorRecord = serverData.filter(x => x.itemtype == 'Tractor');
        tempArray = tempArray.concat(categoriesTractorRecord);
        if (this.othersArray.length > 1) {
          this.othersArray.forEach((rec) => {
            if (rec != 'Others') {
              if (rec == 'Salaries') {
                let SalaryRecords = serverData.filter(x => x.itemtype == rec);
                ;
                let LaborRecord = serverData.filter(x => x.itemtype == 'Labour');
                tempArray = tempArray.concat(SalaryRecords);
                tempArray = tempArray.concat(LaborRecord);
              } else if (rec == 'Applications') {
                let applicationRecords = serverData.filter(x => x.itemtype == 'Treatment');
                let feedmixRecord = serverData.filter(x => x.itemtype == 'Feed Mix');
                let probioticRecord = serverData.filter(x => x.itemtype == 'probiotic');
                let multivitaminRecord = serverData.filter(x => x.itemtype == 'Multi Vitamin');
                let chemicalRecord = serverData.filter(x => x.itemtype == 'Chemical');
                tempArray = tempArray.concat(applicationRecords);
                tempArray = tempArray.concat(feedmixRecord);
                tempArray = tempArray.concat(probioticRecord);
                tempArray = tempArray.concat(multivitaminRecord);
                tempArray = tempArray.concat(chemicalRecord);
              } else if (rec == 'Feed') {
                let FeedRecords = serverData.filter(x => x.itemtype == rec);
                let powderRecord = serverData.filter(x => x.itemtype == 'Powder Feed');
                let liveFeedRecord = serverData.filter(x => x.itemtype == 'Live Feed');
                tempArray = tempArray.concat(FeedRecords);
                tempArray = tempArray.concat(powderRecord);
                tempArray = tempArray.concat(liveFeedRecord);
              } else {
                let otherRecords = serverData.filter(x => x.itemtype == rec);
                tempArray = tempArray.concat(otherRecords);
              }
            }
          });
        }
      } else if (item.categoryName == 'Applications') {
        tempArray = serverData.filter(x => x.itemtype == 'Treatment' || x.itemtype == 'Feed Mix' || x.itemtype == 'probiotic' || x.itemtype == 'Multi Vitamin' || x.itemtype == 'Chemical');
      } else if (item.categoryName == 'Feed') {
        tempArray = serverData.filter(x => x.itemtype == 'Feed' || x.itemtype == 'Powder Feed' || x.itemtype == 'Live Feed');
      } else {
        tempArray = serverData.filter(x => x.itemtype == item.categoryName);
      }
      if (tempArray.length > 6) {
        let val = tempArray.length - 6;
        for (let i = 0; i < val; i++) {
          let col = this.getRandomColor();
          colors.push(col);
        }
        ;
      }
      const resultArr = [];
      let otherVal = 0;
      tempArray.forEach((ele, index) => {
        if (ele.expensecost != null) {
          let record = {
            categoryName: ele.item_name,
            subType: ele.sub_type,
            expense: parseFloat(ele.expensecost.toFixed(2)),
            color: colors[index],
            percentage: parseFloat(((ele.expensecost / item.expense) * 100).toFixed(2)),
            displayExpense: new Intl.NumberFormat('en-IN').format(parseFloat(ele.expensecost.toFixed(2)))
          };
          resultArr.push(record);
        }
      });
      let tempItemArray = [];
      resultArr.forEach((per) => {
        if ((per.percentage < 1 && tempArray.length > 6) || per.categoryName == 'Others') {
          otherVal = otherVal + per.expense;
        } else {
          tempItemArray.push(per);
        }
      });
      if (otherVal > 0) {
        tempItemArray.push({
          categoryName: 'Others',
          expense: parseFloat(otherVal.toFixed(2)),
          color: this.getRandomColor(),
          percentage: parseFloat(((otherVal / item.expense) * 100).toFixed(2)),
          displayExpense: new Intl.NumberFormat('en-IN').format(parseFloat(otherVal.toFixed(2)))
        });
      }
      this.itemsResultsArray = [];
      tempItemArray.forEach((item) => {
        const temp = this.itemsResultsArray.filter(e => {
          return e.categoryName === item.categoryName;
        });
        if (temp.length > 0) {
          this.itemsResultsArray[this.itemsResultsArray.findIndex(e => {
            return e.categoryName === item.categoryName;
          })].expense += item.expense;
        } else {
          this.itemsResultsArray.push(item);
        }
      });
      this.itemsResultsArray.forEach(element => {
        element.displayExpense = new Intl.NumberFormat('en-IN').format(element.expense);
      });
      this.itemsPiecontent = [];
      this.itemsResultsArray.forEach((pie) => {
        this.itemsPiecontent.push({ // preapare array of obejcts for display graph
          'label': pie.categoryName + ' - ' + pie.subType,
          'value': pie.expense,
          'color': pie.color,
        });
      });
      this.itemsPiecontent.sort(function (a, b) {
        a = a.label.toLowerCase();
        b = b.label.toLowerCase();

        return a < b ? -1 : a > b ? 1 : 0;
      });
      let PieValue = item.categoryName + '  : ' + item.expense;
      this.initGraph(PieValue);
    }
  }

  /**
   * @method getRandomColor
   * @description Generates a random hexadecimal color code.
   * Used for coloring chart slices dynamically.
   * @returns {string} Random hex color code.
   */
  public getRandomColor() {
    var letters = '0123456789ABCDEF';
    var color = '#';
    for (var i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  /**
   * @method initGraph
   * @description Initializes and renders the D3.js pie chart for item details (drill-down view).
   * @param {string} PieValue - Title text for the chart center.
   */
  public initGraph(PieValue) {
    let ele = '<div id=\'pieItemChart\'></div>';
    $('#canvasarea').html(ele);
    this.itemPieChart = new d3pie('pieItemChart', {
      'header': {
        'title': {
          'text': PieValue,
          'fontSize': 16,
          'font': 'Arial, Helvetica, sans-serif',
          'color': '#0F4573'
        },
        'location': 'pie-center',
        'titleSubtitlePadding': 10
      },
      'size': {
        'canvasWidth': 790,
        'pieInnerRadius': '80%',
        'pieOuterRadius': '70%'
      },
      'data': {
        'content': this.itemsPiecontent
      },
      tooltips: {
        enabled: false,
      },
      'labels': {
        'outer': {
          'format': 'label-percentage1',
          'pieDistance': 50
        },
        'inner': {
          'format': 'none'
        },
        'mainLabel': {
          'fontSize': 11
        },
        'percentage': {
          'color': '#999999',
          'fontSize': 11,
          'decimalPlaces': 0
        },
        'value': {
          'color': '#cccc43',
          'fontSize': 11
        },
        'lines': {
          'enabled': true,
          'color': '#777777'
        },
        'truncation': {
          'enabled': true
        }
      },
      'effects': {
        'pullOutSegmentOnClick': {
          'effect': 'linear',
          'speed': 400,
          'size': 8
        }
      },
      'misc': {
        colors: {
          segments: [
            '#999999', '#888888', '#777777', '#666666', '#555555', '#444444', '#333333', '#222222'
          ]
        }
      },
    });
  };
  
  /**
   * @method dateRangeField
   * @description Getter for the date range form group.
   * @returns {FormGroup} The date range form group.
   */
  get dateRangeField(): FormGroup {
    return this.registerForm.get('dateRangeField') as FormGroup;
  }

  /**
   * @method initializeDatePicker
   * @description Initializes the date picker with a default range of the past year.
   * Sets the `selected` date range object.
   */
  initializeDatePicker(): void {
    const today = moment().startOf('day').toDate();
    const oneYearAgo = moment().subtract(1, 'year').startOf('day').toDate();
    this.dateRangeField.patchValue(
      {
        start: oneYearAgo,
        end: today
      },
      { emitEvent: false }
    );
    this.selected = {
      startDate: moment(oneYearAgo),
      endDate: moment(today)
    };
  }

  /**
   * @method onPondSelect
   * @description Handles pond selection change.
   * Toggles between single date picker (specific pond) and date range picker (all ponds).
   */
  onPondSelect(): void {
    const selectedPond = this.registerForm.get('pond')?.value;
    const today = moment().startOf('day').toDate();

    if (selectedPond === '') {
      // "All" selected → enable date range for past 1 year
      const oneYearAgo = moment().subtract(1, 'year').startOf('day').toDate();

      this.singleDatePickerFlag = false; // show date range
      this.dateRangeField.patchValue(
        { start: oneYearAgo, end: today },
        { emitEvent: false }
      );
      this.selected = {
        startDate: moment(oneYearAgo),
        endDate: moment(today)
      };
    } else {
      // Specific pond selected → use single date
      this.singleDatePickerFlag = true;

      this.dateRangeField.patchValue(
        { start: today, end: today },
        { emitEvent: false }
      );
      this.selected = {
        startDate: moment(today),
        endDate: moment(today)
      };
      this.transactionDateControl.setValue(today);
    }
  }

  /**
   * @method onPickerClosed
   * @description Handles the closing of the date picker. Ensures valid range selection.
   */
  onPickerClosed(): void {
    const { start, end } = this.dateRangeField.value;

    if (start && !end) {
      this.dateRangeField.patchValue({ end: start }, { emitEvent: false });
      this.onDateChanged({ startDate: start, endDate: start });
    } else if (start && end) {
      this.onDateChanged({ startDate: start, endDate: end });
    }
  }

  /**
   * @method onDateChanged
   * @description Updates the `selected` date range object when dates change.
   * @param {{ startDate: Date; endDate: Date }} range - The new date range.
   */
  onDateChanged(range: { startDate: Date; endDate: Date }): void {
    this.selected = {
      startDate: moment(range.startDate),
      endDate: moment(range.endDate)
    };
  }

}
